import React, { useState, useEffect, useRef } from "react";
import { CloseOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { notification } from "antd";
import { CircularProgress } from "@material-ui/core";
import styles from "./SwmsWizard.module.css";
import SwmsWizardStep1 from "./SwmsWizardStep1";
import SwmsWizardStep2 from "./SwmsWizardStep2";
import SwmsWizardStep3 from "./SwmsWizardStep3";
import { getSwmsCategories, createSwmsDraft } from "../../../../../actions/v2/JobSwmsTaskActions";
import { Strings } from "../../../../../dataProvider/localize";

export default function SwmsWizard({ onClose, jobId }) {
  const dispatch = useDispatch();
  const parentTasks = useSelector((state) => state.Job.parentTasks);
  const qlis = Array.isArray(parentTasks) && parentTasks.length > 0
    ? parentTasks.map((task) => ({
        id: task.task_id,
        qliTaskName: task.qli_task_name,
        taskName: task.task_name,
        areas: Array.isArray(task.task_areas) ? task.task_areas.map((a) => a.area_name) : []
      }))
    : [];
  const abortControllerRef = useRef();

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [methodsByCategory, setMethodsByCategory] = useState({});
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedMethods, setSelectedMethods] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [expandedQLIs, setExpandedQLIs] = useState(["qli-1"]);
  const [showCatValidation, setShowCatValidation] = useState(false);

  // When qlis data changes, expand the first item by default
  useEffect(() => {
    if (qlis.length > 0) {
      setExpandedQLIs([qlis[0].id]);
    }
  }, [qlis.length]);
  const [showMethodsValidation, setShowMethodsValidation] = useState(false);
  const [showAreasValidation, setShowAreasValidation] = useState(false);

  // Fetch SWMS categories on component mount
  useEffect(() => {
    const fetchCategories = () => {
      abortControllerRef.current = new AbortController();
      setLoading(true);
      dispatch(getSwmsCategories({ signal: abortControllerRef.current.signal }, { pagination: 0, active: 1 })).then((res) => {
        if (res && res.rows) {
          setCategories(res.rows);
          // Build methods by category mapping
          const methodsMap = {};
          res.rows.forEach((category) => {
            methodsMap[category.id] = category.methods || [];
          });
          setMethodsByCategory(methodsMap);
        }
        setLoading(false);
      }).catch((err) => {
        setLoading(false);
        notification.error({
          message: Strings.error_title,
          description: err ? err : "There was an error while fetching SWMS categories.",
          className: "ant-error"
        });
      });
    };

    fetchCategories();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [dispatch]);

  const toggleCategory = (id) => {
    setShowCatValidation(false);
    const isSelected = selectedCategories.includes(id);
    if (isSelected) {
      setSelectedCategories(selectedCategories.filter((c) => c !== id));
    } else {
      setSelectedCategories([...selectedCategories, id]);
    }
  };

  const toggleMethod = (method) => {
    setShowMethodsValidation(false);
    const isSelected = selectedMethods.includes(method);
    if (isSelected) {
      setSelectedMethods(selectedMethods.filter((m) => m !== method));
    } else {
      setSelectedMethods([...selectedMethods, method]);
    }
  };

  const handleContinueStep1 = () => {
    if (selectedCategories.length === 0) {
      setShowCatValidation(true);
      return;
    }
    setCurrentStep(2);
  };

  const handleContinueStep2 = () => {
    if (selectedMethods.length === 0) {
      setShowMethodsValidation(true);
      return;
    }
    setCurrentStep(3);
  };

  const toggleQLIExpand = (qliId) => {
    const isExpanded = expandedQLIs.includes(qliId);
    if (isExpanded) {
      setExpandedQLIs(expandedQLIs.filter((id) => id !== qliId));
    } else {
      setExpandedQLIs([...expandedQLIs, qliId]);
    }
  };

  const toggleArea = (area) => {
    setShowAreasValidation(false);
    const isSelected = selectedAreas.includes(area);
    if (isSelected) {
      setSelectedAreas(selectedAreas.filter((a) => a !== area));
    } else {
      setSelectedAreas([...selectedAreas, area]);
    }
  };

  const toggleQLIAllAreas = (qliId, e) => {
    e.stopPropagation();
    setShowAreasValidation(false);
    const qli = qlis.find((q) => q.id === qliId);
    if (!qli) return;

    const areasInQLI = qli.areas;
    const allAreasSelected = areasInQLI.every((area) => selectedAreas.includes(area));

    if (allAreasSelected) {
      // Deselect all areas in this QLI
      setSelectedAreas(selectedAreas.filter((area) => !areasInQLI.includes(area)));
    } else {
      // Select all areas in this QLI
      const newAreas = [...selectedAreas];
      areasInQLI.forEach((area) => {
        if (!newAreas.includes(area)) {
          newAreas.push(area);
        }
      });
      setSelectedAreas(newAreas);
    }
  };

  const handleContinueStep3 = () => {
    if (selectedAreas.length === 0) {
      setShowAreasValidation(true);
      return;
    }
    setSubmitting(true);
    const abortController = new AbortController();
    // Get unique tasks for areas that were selected.
    const taskMap = new Map();
    selectedAreas.forEach((area) => {
      const qli = qlis.find((q) => q.areas.includes(area));
      if (qli) {
        taskMap.set(qli.id, {
          id: qli.id,
          name: qli.taskName || "",
          display_name: qli.qliTaskName || ""
        });
      }
    });
    const selectedCategoryData = selectedCategories
      .map((catId) => categories.find((category) => category.id === catId))
      .filter(Boolean)
      .map((category) => ({
        id: category.id,
        name: category.category_name || ""
      }));
    const selectedMethodData = [];
    const selectedMethodIds = new Set();
    Object.values(methodsByCategory).forEach((methods) => {
      methods.forEach((method) => {
        if (selectedMethods.includes(method.method_name) && !selectedMethodIds.has(method.id)) {
          selectedMethodIds.add(method.id);
          selectedMethodData.push({
            id: method.id,
            name: method.method_name || ""
          });
        }
      });
    });
    const swmsData = {
      categories: selectedCategoryData,
      methods: selectedMethodData,
      tasks: Array.from(taskMap.values()),
      areas: selectedAreas
    };
    dispatch(createSwmsDraft({ signal: abortController.signal }, jobId, swmsData)).then((res) => {
      setSubmitting(false);
      notification.success({
        message: Strings.success_title,
        description: res && res.message ? res.message : "SWMS draft created successfully.",
        className: "ant-success"
      });
      onClose();
    }).catch((err) => {
      setSubmitting(false);
      notification.error({
        message: Strings.error_title,
        description: err ? err : "There was an error while creating the SWMS draft.",
        className: "ant-error"
      });
    });
  };

  // Build CAT_LABELS dynamically from API response
  const buildCatLabels = () => {
    const labels = {};
    categories.forEach((cat) => {
      labels[cat.id] = cat.category_name || "";
    });
    return labels;
  };

  const catLabels = buildCatLabels();

  // Pre-compute stepper classes
  const step1CircleClass = currentStep === 1
    ? `${styles.stepCircle} ${styles.active}`
    : `${styles.stepCircle} ${styles.done}`;
  const step2CircleClass = currentStep < 2
    ? `${styles.stepCircle} ${styles.inactive}`
    : currentStep === 2
    ? `${styles.stepCircle} ${styles.active}`
    : `${styles.stepCircle} ${styles.done}`;
  const step3CircleClass = currentStep < 3
    ? `${styles.stepCircle} ${styles.inactive}`
    : `${styles.stepCircle} ${styles.active}`;

  const step1LabelClass = currentStep >= 1
    ? `${styles.stepLabel} ${styles.active}`
    : `${styles.stepLabel} ${styles.inactive}`;
  const step2LabelClass = currentStep === 2
    ? `${styles.stepLabel} ${styles.active}`
    : `${styles.stepLabel} ${styles.inactive}`;
  const step3LabelClass = currentStep === 3
    ? `${styles.stepLabel} ${styles.active}`
    : `${styles.stepLabel} ${styles.inactive}`;

  const conn1Class = currentStep > 1
    ? `${styles.stepConnector} ${styles.stepConnectorDone}`
    : styles.stepConnector;

  const conn2Class = currentStep > 2
    ? `${styles.stepConnector} ${styles.stepConnectorDone}`
    : styles.stepConnector;

  const step1CircleContent = currentStep > 1 ? "✓" : "1";
  const step2CircleContent = currentStep > 2 ? "✓" : "2";

  // Show loading spinner
  if (loading) {
    return (
      <div className={styles.overlay}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh"
        }}>
          <CircularProgress size={40} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.overlay}>
      {/* ── Header ── */}
      <div className={styles.wizardHeader}>
        {/* Breadcrumb + Close */}
        <div className={styles.breadcrumbRow}>
          <div className={styles.breadcrumbNav}>
            <button className={styles.breadcrumbBack} onClick={onClose}>
              <ArrowLeftOutlined />
              All SWMS
            </button>
            <span className={styles.breadcrumbSep}>›</span>
            <span className={styles.breadcrumbCurrent}>Create SWMS</span>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            <CloseOutlined />
          </button>
        </div>

        {/* Stepper */}
        <div className={styles.stepperRow}>
          <div className={styles.stepper}>
            <div className={styles.stepItem}>
              <div className={step1CircleClass}>{step1CircleContent}</div>
              <span className={step1LabelClass}>Category</span>
            </div>
            <div className={conn1Class} />
            <div className={styles.stepItem}>
              <div className={step2CircleClass}>{step2CircleContent}</div>
              <span className={step2LabelClass}>Methods</span>
            </div>
            <div className={conn2Class} />
            <div className={styles.stepItem}>
              <div className={step3CircleClass}>3</div>
              <span className={step3LabelClass}>QLIs</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Step Content ── */}
      {currentStep === 1 && (
        <SwmsWizardStep1
          categories={categories}
          catLabels={catLabels}
          selectedCategories={selectedCategories}
          showValidation={showCatValidation}
          onToggleCategory={toggleCategory}
          onContinue={handleContinueStep1}
        />
      )}

      {currentStep === 2 && (
        <SwmsWizardStep2
          selectedCategories={selectedCategories}
          selectedMethods={selectedMethods}
          methodsByCategory={methodsByCategory}
          catLabels={catLabels}
          showValidation={showMethodsValidation}
          onToggleMethod={toggleMethod}
          onContinue={handleContinueStep2}
          onBack={() => setCurrentStep(1)}
        />
      )}

      {currentStep === 3 && (
        <SwmsWizardStep3
          selectedCategories={selectedCategories}
          selectedMethods={selectedMethods}
          selectedAreas={selectedAreas}
          expandedQLIs={expandedQLIs}
          catLabels={catLabels}
          showValidation={showAreasValidation}
          submitting={submitting}
          onToggleQLIExpand={toggleQLIExpand}
          onToggleArea={toggleArea}
          onToggleQLIAllAreas={toggleQLIAllAreas}
          onContinue={handleContinueStep3}
          onBack={() => setCurrentStep(2)}
          onCancel={onClose}
        />
      )}
    </div>
  );
}
